// Slice 43 — Create-customer modal. Lawful-basis is required up-front so
// every customer row has a non-null GDPR rationale from creation; the
// privacy card on the detail modal then has something to explain.

import { useEffect, useState } from "react";
import { CustomersApi, LAWFUL_BASES, type LawfulBasis } from "../../api/client";
import { HqButton, HqInput, HqModal, HqRadio, Icons } from "../../ui";

const BASIS_BLURB: Record<LawfulBasis, { label: string; blurb: string }> = {
  contract: {
    label: "Contract",
    blurb: "Paid customer · Art. 6(1)(b). Processing necessary to deliver the licensed software.",
  },
  consent: {
    label: "Consent",
    blurb: "Free / community user · Art. 6(1)(a). Opted in explicitly.",
  },
  legitimate_interest: {
    label: "Legitimate interest",
    blurb: "Art. 6(1)(f). Anti-fraud / abuse handling. Documented balancing test required.",
  },
};

export interface CreateCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (newCustomerId: string | null) => void;
}

export function CreateCustomerModal({ open, onClose, onCreated }: CreateCustomerModalProps) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [country, setCountry] = useState("");
  const [notes, setNotes] = useState("");
  const [basis, setBasis] = useState<LawfulBasis>("contract");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setDisplayName("");
      setCountry("");
      setNotes("");
      setBasis("contract");
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  async function submit() {
    setErr(null);
    const e = email.trim();
    if (!e.includes("@")) {
      setErr("Email looks malformed.");
      return;
    }
    setBusy(true);
    const res = await CustomersApi.create({
      email: e,
      lawfulBasis: basis,
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      ...(country.trim() ? { country: country.trim().toUpperCase() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onCreated(res.data?.id ?? null);
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Users width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
          New customer
        </span>
      }
      width={520}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            leadingIcon={Icons.Check}
            loading={busy}
            onClick={() => void submit()}
          >
            Create
          </HqButton>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Email" hint="Stored raw; list/detail surfaces only the masked form.">
          <HqInput
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="kjetil@example.com"
            autoFocus
          />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 12 }}>
          <Field label="Display name">
            <HqInput
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional"
            />
          </Field>
          <Field label="Country" hint="ISO-3166-1 a2">
            <HqInput
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="NO"
              mono
            />
          </Field>
        </div>
        <Field label="Lawful basis (GDPR Art. 6)">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {LAWFUL_BASES.map((b) => (
              <BasisCard key={b} value={b} active={basis === b} onClick={() => setBasis(b)} />
            ))}
          </div>
        </Field>
        <Field label="Internal note" hint="HQ-only.">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              background: "var(--hq-bg-input)",
              color: "var(--hq-text)",
              border: "1px solid var(--hq-border-input)",
              borderRadius: "var(--hq-radius-lg)",
              padding: "10px 12px",
              fontSize: 12.5,
              fontFamily: "var(--hq-font-sans)",
              resize: "vertical",
              outline: "none",
            }}
          />
        </Field>
        {err && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: "var(--hq-radius-md)",
              background: "var(--hq-danger-soft)",
              color: "var(--hq-danger)",
              fontSize: 12,
              border: "1px solid rgba(251,113,133,.25)",
            }}
          >
            {err}
          </div>
        )}
      </div>
    </HqModal>
  );
}

function BasisCard({
  value,
  active,
  onClick,
}: {
  value: LawfulBasis;
  active: boolean;
  onClick: () => void;
}) {
  const cfg = BASIS_BLURB[value];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 12,
        borderRadius: "var(--hq-radius-lg)",
        background: active ? "var(--hq-bg-active)" : "var(--hq-bg-input)",
        border: `1px solid ${active ? "rgba(34,211,238,.45)" : "var(--hq-border-input)"}`,
        color: "var(--hq-text)",
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
      }}
    >
      <HqRadio checked={active} onChange={() => onClick()} ariaLabel={cfg.label} />
      <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{cfg.label}</span>
        <span style={{ fontSize: 11, color: "var(--hq-text-dim)", lineHeight: 1.45 }}>
          {cfg.blurb}
        </span>
      </span>
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)", fontWeight: 500 }}>
          {label}
        </span>
        {hint && <span style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}
